import { useMemo } from "react";
import { TableColumnType, theme } from "antd";
import type { GlobalToken } from "antd/lib/theme/interface";
import { Api } from "@vzbl/integrations";
import { SearchContext } from "src/search/useSearchContext";
import { SearchState } from "src/search/types";
import { asKey, fromKey } from "src/utils/fields";
import { Field } from "src/search/types";
import {
  attributeToTitle,
  formatValue,
  unitTypeForCol,
  formatTitle,
} from "src/utils/strings";
import { TimeUnit } from "../FormattingControls";
import { AggregationConfig, UIAggregation } from "../types";
import { ChangeColor } from "../CompareWithControls";
import { SortOrder } from "antd/lib/table/interface";
import { GroupedTableHeader } from "pages/[orgId]/explore/lib/table/TableHeader";
import { ContentView } from "../useContentView";
import { useMeasureTableValue } from "./useMeasureTableValue";
import { ComparisonConfig, parseComparisonValue } from "./useComparisonValue";
import { ComparisonCell } from "../components/ComparisonCell";
import { MeasureCell } from "../components/MeasureCell";
import { TableColumnHeaderTooltip } from "../tooltips/VZBLTooltips";

interface UseMeasureTableColumnsProps {
  context: SearchContext & { view: ContentView };
  config: {
    columns: string[];
    sortBy?: string;
    sortDirection?: "ascending" | "descending";
    timeUnit?: TimeUnit;
    showChangeAs?: "value" | "percentage";
    showChangeValues?: boolean;
    changeColor?: ChangeColor;
  } & Partial<ComparisonConfig>;
  token?: GlobalToken;
}

function FormattedMeasureCell({
  value,
  field,
  context,
  columnInfo,
  timeUnit,
}: {
  value: any;
  field: Field;
  context: SearchContext & { view: ContentView };
  columnInfo: any;
  timeUnit?: TimeUnit;
}) {
  const formattedValue = useMeasureTableValue({
    context,
    field,
    value,
    columnInfo,
    timeUnit,
  });

  return (
    <MeasureCell
      rawValue={value}
      formattedValue={formattedValue}
      colName={field.attribute}
      field={field}
      aggregations={context.params.state.aggregations || []}
      formulas={context.params.state.formulas || []}
      columnInfo={columnInfo}
      config={context.params.state}
    />
  );
}

export function useMeasureTableColumns({
  context,
  config,
  token,
}: UseMeasureTableColumnsProps) {
  const { params, aggregations } = context;
  const columnInfo = useMemo(() => {
    const baseColumnInfo = aggregations.data?.meta?.columnInfo || [];
    const formulas = params.state.formulas || [];
    const diffMethod = params.state.compareWith?.method || "";

    // If there are no formulas or no comparison, return base columnInfo
    if (formulas.length === 0 || diffMethod !== "tuple") {
      return baseColumnInfo;
    }

    // Add formula columns with diffMethod if comparison is active
    const formulaColumns = formulas.map((formula) => ({
      key: formula.name,
      type: "number", // Formulas typically output numbers
      target: formula.name,
      attribute: formula.name,
      diffMethod: "tuple", // Set diffMethod for formulas when comparison is active
      reducer: undefined, // Add optional reducer property
    }));

    return [...baseColumnInfo, ...formulaColumns];
  }, [
    aggregations.data?.meta?.columnInfo,
    params.state.formulas,
    params.state.compareWith?.method,
  ]);

  const getFormulaColumnConfig = (
    formula: Exclude<SearchState["formulas"], undefined>[number]
  ) => {
    const field = fromKey(formula.name);

    const { type, unit, tuple } = unitTypeForCol(formula.name, columnInfo);
    const title = formatTitle(formula.name);

    return {
      ...getColumnConfig(
        formula.name,
        title,
        title,
        formula.name,
        columnInfo,
        config,
        token,
        config.timeUnit
      ),
      title: (
        <TableColumnHeaderTooltip
          formula={formula.formula ? formula.formula.join(" ") : ""}
          field={field}
          title={title}
          type="formula"
        />
      ),
      render: (_: any, record: Record<string, any>) => {
        const value = record?.[formula.name];
        const formattedValue = formatValue(value, type, unit, config.timeUnit);

        if (tuple) {
          return (
            <ComparisonCell
              value={parseComparisonValue(value)}
              type={type}
              unit={unit}
              showChangeAs={config.showChangeAs}
              showChangeValues={config.showChangeValues}
              changeColor={config.changeColor}
              timeUnit={config.timeUnit}
              token={token}
            />
          );
        }

        return (
          <MeasureCell
            rawValue={value}
            formattedValue={formattedValue}
            colName={formula.name}
            field={field}
            aggregations={params.state.aggregations || []}
            formulas={params.state.formulas || []}
            columnInfo={columnInfo}
            config={config}
          />
        );
      },
    };
  };

  const getAggColumnConfig = (
    aggregation: UIAggregation | undefined,
    colName: string
  ) => {
    const field = fromKey(colName);

    const title = formatTitle(colName);

    // Check if this aggregation has filters
    const filters = aggregation?.filters || [];
    const hasFilters = filters.length > 0;
    const filterSuffix = hasFilters
      ? `_f:${filters[0].target}.${filters[0].attribute}:${
          Object.keys(filters[0].comparison || {})[0]
        }:${Object.values(filters[0].comparison || {})[0]}`
      : "";

    const aggregationConfig: AggregationConfig = {
      type: "value",
      field: aggregation?.attribute || field.attribute,
      key: `${asKey(aggregation!)}${filterSuffix}`,
      title,
    };

    return {
      ...getColumnConfig(
        `${asKey(aggregation!)}${filterSuffix}`,
        title,
        title,
        colName,
        columnInfo,
        config,
        token,
        config.timeUnit
      ),
      title: (
        <TableColumnHeaderTooltip
          field={field}
          title={title}
          type="aggregation"
          aggregation={aggregationConfig}
        />
      ),
    };
  };

  const getColumnConfig = (
    key: string,
    header: string,
    tooltip: string,
    colName: string,
    columnInfo: Api.Schema.ColumnInfo,
    config: UseMeasureTableColumnsProps["config"],
    colorize?: GlobalToken,
    timeUnit?: TimeUnit
  ) => {
    const { type, unit, tuple } = unitTypeForCol(colName, columnInfo);

    const field = fromKey(colName);

    const aggregationConfig: AggregationConfig = {
      type: tuple ? "comparison" : "value",
      field: field.attribute,
      key: key,
      title: header,
    };

    const title =
      type === "number" && unit ? (
        <TableColumnHeaderTooltip
          field={field}
          title={header}
          type="aggregation"
          aggregation={aggregationConfig}
        />
      ) : (
        <TableColumnHeaderTooltip field={field} title={header} type="default" />
      );

    return {
      key,
      dataIndex: key,
      sorter: true,
      showSorterTooltip: false,
      sortOrder:
        config.sortBy === key
          ? ((config.sortDirection === "ascending"
              ? "ascend"
              : "descend") as SortOrder)
          : undefined,
      title,
      render(_: any, record: Record<string, any>) {
        const lookup = colName;
        const rawValue = record[lookup || ""];

        if (tuple) {
          return (
            <ComparisonCell
              value={parseComparisonValue(rawValue)}
              type={type}
              unit={unit}
              showChangeAs={config.showChangeAs}
              showChangeValues={config.showChangeValues}
              changeColor={config.changeColor}
              timeUnit={config.timeUnit}
              token={colorize}
            />
          );
        }

        // Non-tuple case: simple value formatting
        const formattedValue = formatValue(rawValue, type, unit, timeUnit);

        return (
          <MeasureCell
            rawValue={rawValue}
            formattedValue={formattedValue}
            colName={colName}
            field={field}
            aggregations={params.state.aggregations || []}
            formulas={params.state.formulas || []}
            columnInfo={columnInfo}
            config={config}
          />
        );
      },
    };
  };

  const columns = useMemo(() => {
    const cols: TableColumnType<any>[] = (params.state.groupBys || []).map(
      (g, i) => ({
        key: asKey(g),
        title: (
          <GroupedTableHeader
            field={g}
            index={i}
            params={params}
            title={g.attribute || g.target || "Group"}
            columnInfo={columnInfo}
          />
        ),
        dataIndex: asKey(g),
        sorter: true,
        sortOrder:
          config.sortBy === asKey(g)
            ? config.sortDirection === "ascending"
              ? "ascend"
              : "descend"
            : undefined,
        render: (_: string, rec: Record<string, Api.FieldValue>) => {
          const value = rec[asKey(g)];
          return (
            <FormattedMeasureCell
              value={value}
              field={g}
              context={context}
              columnInfo={columnInfo}
              timeUnit={config.timeUnit}
            />
          );
        },
      })
    );

    if (params.state.aggregations?.length) {
      params.state.aggregations.forEach((aggregation) => {
        const colName = params.selectors.getColumnForAggregation(
          params.state,
          aggregation
        );
        const aggColumn = getAggColumnConfig(aggregation, colName);
        cols.push(aggColumn);
      });
    } else {
      // no aggregation in query state, add aggregations we come across
      const aggregations = columnInfo.filter((c) => c.reducer);
      if (aggregations?.length) {
        aggregations.forEach((aggregation) => {
          const uiAggregation: UIAggregation = {
            target: aggregation.target,
            attribute: aggregation.attribute,
            key: aggregation.key,
          };
          cols.push(getAggColumnConfig(uiAggregation, aggregation.key));
        });
      }
    }

    // formulas
    const formulas = params.state.formulas || [];
    if (formulas.length > 0) {
      formulas.forEach((formula) => {
        const formulaColumn = getFormulaColumnConfig(formula);
        cols.push(formulaColumn);
      });
    }

    return cols
      .filter((col) => {
        const dataIndexStr = `${col.dataIndex}`;
        const keyStr = `${col.key}`;

        // Check if any config column starts with our dataIndex or key
        // This handles both filtered and unfiltered versions
        const matchingConfigColumn = config.columns.find(
          (configCol) =>
            configCol.startsWith(dataIndexStr) || configCol.startsWith(keyStr)
        );

        return config.columns.length === 0 || Boolean(matchingConfigColumn);
      })
      .sort((a, b) => {
        const aKey = `${a.key}`;
        const bKey = `${b.key}`;

        // Find matching config columns for sorting
        const aConfigCol = config.columns.find((c) => c.startsWith(aKey));
        const bConfigCol = config.columns.find((c) => c.startsWith(bKey));

        const aIndex = aConfigCol ? config.columns.indexOf(aConfigCol) : -1;
        const bIndex = bConfigCol ? config.columns.indexOf(bConfigCol) : -1;

        return config.columns.length === 0 ? 0 : aIndex - bIndex;
      });
  }, [params.state, columnInfo, config, token]);

  return {
    columns,
    columnInfo,
  };
}
